import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";import api from "@/lib/api";
import useAffiliateTracker from "@/lib/useAffiliateTracker";
import SuggestedKits from "@/components/SuggestedKits";
import { formatApiError, useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from "recharts";
import {
  Loader2, Lock, ShieldCheck, AlertTriangle, Info, CheckCircle2,
  ArrowRight, Droplets, Flame, Sparkles, Cpu, ChevronRight, Zap, Waves,
  Euro, Gauge, Camera, Trash2, ImagePlus, Brain, Wand2, ShoppingBag, MapPin,
  FileText, Wrench,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORY_ICONS = { general: Info, pump: Droplets, heating: Flame, water: Sparkles, electronic: Cpu, leak: Waves };
const CATEGORY_LABELS = {
  general: "Général", pump: "Pompe & Filtration", heating: "Chauffage",
  water: "Qualité de l'eau", electronic: "Électronique", leak: "Fuite d'eau",
};
const SEVERITY = {
  critical: { color: "bg-red-100 text-red-700 border-red-200", label: "Critique", icon: AlertTriangle },
  warning: { color: "bg-amber-100 text-amber-700 border-amber-200", label: "Attention", icon: Zap },
  info: { color: "bg-blue-100 text-blue-700 border-blue-200", label: "Info", icon: Info },
};

const KIT_CATEGORY_FROM_DIAG = {
  heating: "heating", pump: "pump", electronic: "electronics",
  water: "water", leak: "other", general: null,
};

export default function Results() {
  useAffiliateTracker({ context: "diagnostic_result" });
  const { id } = useParams();
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await api.get(`/diagnostic/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `doctospa-diagnostic-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF téléchargé");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDownloadingPdf(false);
    }
  };
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = () => {
    api.get(`/diagnostic/${id}`)
      .then(({ data }) => setDiag(data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const buyReport = async () => {
    setCheckingOut(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "report_onetime",
        origin_url: window.location.origin,
        diagnostic_id: id,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setCheckingOut(false);
    }
  };

  const unlockWithCode = async (code) => {
    try {
      await api.post(`/diagnostic/${id}/unlock`, { code });
      toast.success("Rapport débloqué !");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) {
    return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }
  if (!diag) return null;

  const r = diag.result;
  const isPaid = diag.paid || user?.subscription_status === "active";
  const scoreColor = r.overall_score >= 75 ? "text-emerald-600" : r.overall_score >= 50 ? "text-amber-600" : "text-red-600";

  const chartData = Object.entries(r.category_scores || {}).map(([k, v]) => ({
    name: CATEGORY_LABELS[k] || k, score: v, key: k,
  }));

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12" data-testid="results-page">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Link to="/dashboard" className="hover:text-primary">Tableau de bord</Link>
        <ChevronRight className="h-3 w-3" />
        <span>Diagnostic</span>
      </div>

      <div className="mt-4 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="eyebrow">Résultat du diagnostic</div>
          <h1 className="mt-2 font-display text-3xl lg:text-4xl font-bold tracking-tight">{r.headline}</h1>
        </div>
        <div className="flex gap-2">
          <Link to="/diagnostic"><Button variant="outline" className="rounded-full" data-testid="new-diagnostic-btn">Nouveau diagnostic</Button></Link>
          {!isPaid && (
            <Button onClick={buyReport} disabled={checkingOut} className="rounded-full" data-testid="buy-report-btn">
              {checkingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Débloquer le rapport <ArrowRight className="h-4 w-4 ml-2" /></>}
            </Button>
          )}
        </div>
      </div>

      {/* KPI Grid - dashboard high-density */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
        {/* Hero KPI */}
        <Card className="md:col-span-2 lg:col-span-2 row-span-2 p-8 border-black/10 relative overflow-hidden" data-testid="score-card">
          <div className="absolute inset-0 grid-pattern-dense opacity-40 pointer-events-none" />
          <div className="relative">
            <div className="eyebrow">Score global</div>
            <div className="mt-4 flex items-baseline gap-3">
              <span className={`font-display text-7xl lg:text-8xl font-black tracking-tighter ${scoreColor}`}>
                {r.overall_score}
              </span>
              <span className="text-lg text-muted-foreground font-medium">/100</span>
            </div>
            <div className="mt-4">
              <HealthBadge health={r.health} />
              {r.probability && (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-black/10 bg-white px-3 py-1 text-xs font-semibold text-foreground">
                  <Gauge className="h-3 w-3" /> {r.probability}
                </span>
              )}
            </div>
            {r.estimate && (
              <div className="mt-5 inline-flex items-center gap-2 rounded-xl bg-primary/10 px-4 py-2 text-primary">
                <Euro className="h-4 w-4" />
                <span className="text-sm font-semibold">Estimation : {r.estimate}</span>
              </div>
            )}
            <p className="mt-6 text-sm text-muted-foreground max-w-md leading-relaxed">
              Ce score reflète l'état général de votre spa et met en avant le diagnostic
              identifié via l'arbre de décision technique DoctoSpa.
            </p>
            {isPaid && (
              <Button
                onClick={downloadPdf}
                disabled={downloadingPdf}
                variant="outline"
                className="mt-6 rounded-full"
                data-testid="download-pdf-btn"
              >
                {downloadingPdf ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
                Télécharger en PDF
              </Button>
            )}
          </div>
        </Card>

        {/* KPI cards */}
        <Card className="p-6 border-black/10" data-testid="issues-count-card">
          <div className="eyebrow">Problèmes</div>
          <div className="mt-3 font-display text-4xl font-black">{r.total_issues}</div>
          <p className="mt-1 text-xs text-muted-foreground">points identifiés</p>
        </Card>
        <Card className="p-6 border-black/10">
          <div className="eyebrow">Critiques</div>
          <div className="mt-3 font-display text-4xl font-black text-red-600">
            {isPaid
              ? (r.issues || []).filter((i) => i.severity === "critical").length
              : ((r.issues_preview || []).filter((i) => i.severity === "critical").length || "—")}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">à traiter d'urgence</p>
        </Card>

        {/* Category scores chart */}
        <Card className="md:col-span-3 lg:col-span-4 p-6 border-black/10" data-testid="category-chart-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eyebrow">Score par catégorie</div>
              <h3 className="mt-1 font-display text-lg font-semibold">Répartition technique</h3>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ left: 12, right: 20, top: 8, bottom: 8 }}>
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12, fill: "hsl(var(--foreground))" }} axisLine={false} tickLine={false} />
                <Bar dataKey="score" radius={[0, 6, 6, 0]}>
                  {chartData.map((d) => {
                    const fill = d.score >= 75 ? "#10B981" : d.score >= 50 ? "#F59E0B" : "#EF4444";
                    return <Cell key={d.key} fill={fill} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Spa context + Photos */}
      <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {(diag.spa_brand || diag.spa_model || diag.problem_description) && (
          <Card className="p-6 border-black/10" data-testid="spa-context-card">
            <div className="eyebrow">Contexte spa</div>
            <div className="mt-3 space-y-3 text-sm">
              {diag.spa_brand && (
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Marque</span>
                  <span className="font-semibold capitalize">{diag.spa_brand}</span>
                </div>
              )}
              {diag.spa_model && (
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Modèle</span>
                  <span className="font-semibold">{diag.spa_model}</span>
                </div>
              )}
              {diag.problem_description && (
                <div className="pt-3 border-t border-black/5">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Description</div>
                  <p className="text-sm text-foreground leading-relaxed">{diag.problem_description}</p>
                </div>
              )}
            </div>
          </Card>
        )}
        <div className={diag.spa_brand || diag.spa_model || diag.problem_description ? "lg:col-span-2" : "lg:col-span-3"}>
          <PhotoUploadSection diagId={id} diag={diag} onChange={setDiag} />
        </div>
      </div>

      {/* Issues section */}
      <div className="mt-12">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="eyebrow">Détails</div>
            <h2 className="mt-1 font-display text-2xl font-bold">Problèmes détectés</h2>
          </div>
        </div>

        {isPaid ? (
          <div className="space-y-4">
            {(r.issues || []).length === 0 ? (
              <Card className="p-8 border-black/10 flex items-center gap-4">
                <span className="h-12 w-12 rounded-full bg-emerald-100 grid place-items-center text-emerald-700">
                  <CheckCircle2 className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="font-display text-lg font-semibold">Tout est en ordre.</h3>
                  <p className="text-sm text-muted-foreground">Poursuivez votre routine de maintenance habituelle.</p>
                </div>
              </Card>
            ) : (
              (r.issues || []).map((issue, idx) => <IssueCard key={idx} issue={issue} full />)
            )}
          </div>
        ) : (
          <PaywallSection preview={r.issues_preview} onBuy={buyReport} loading={checkingOut} onUnlock={unlockWithCode} />
        )}
      </div>

      {/* AI Vision (Claude Sonnet 4.5) */}
      {isPaid && (
        <AIVisionSection diagId={id} diag={diag} onChange={setDiag} />
      )}

      {/* Suggested parts — pièces à commander pour ce type de panne */}
      {isPaid && (r.suggested_parts || []).length > 0 && (
        <SuggestedPartsSection parts={r.suggested_parts} />
      )}

      {/* Next actions — spare parts + technicians */}
      {isPaid && (
        <NextActions diag={diag} />
      )}

      {/* Kit de réparation recommandé — fin du rapport */}
      <div data-testid="recommended-kit-section">
        <SuggestedKits
          brand={diag.spa_brand}
          category={KIT_CATEGORY_FROM_DIAG[(r.issues?.[0] || r.issues_preview?.[0])?.category] || undefined}
          limit={3}
          title="Le kit de réparation compatible avec votre panne"
          subtitle="Toutes les pièces nécessaires pour cette réparation, testées et livrées dans un même carton — avec notice pas-à-pas."
        />
      </div>
    </div>
  );
}

function HealthBadge({ health }) {
  const map = {
    critical: { label: "Critique", cls: "bg-red-100 text-red-700 border-red-200" },
    warning: { label: "Attention", cls: "bg-amber-100 text-amber-700 border-amber-200" },
    info: { label: "Recommandations", cls: "bg-blue-100 text-blue-700 border-blue-200" },
    healthy: { label: "Sain", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  };
  const b = map[health] || map.info;
  return <Badge variant="outline" className={`${b.cls} font-semibold px-3 py-1 rounded-full`}>{b.label}</Badge>;
}

function IssueCard({ issue, full }) {
  const s = SEVERITY[issue.severity] || SEVERITY.info;
  const Cat = CATEGORY_ICONS[issue.category] || Info;
  const Sev = s.icon;
  return (
    <Card className="p-6 border-black/10 hover:shadow-sm transition-shadow" data-testid={`issue-${issue.severity}`}>
      <div className="flex items-start gap-4">
        <span className="grid place-items-center h-11 w-11 rounded-lg bg-muted text-foreground">
          <Cat className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={`${s.color} border font-semibold rounded-full`}>
              <Sev className="h-3 w-3 mr-1" />{s.label}
            </Badge>
            <span className="text-xs text-muted-foreground">{CATEGORY_LABELS[issue.category] || issue.category}</span>
          </div>
          <h3 className="mt-2 font-display text-lg font-semibold">{issue.title}</h3>
          {full && (
            <>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="eyebrow">Causes probables</div>
                  <p className="mt-1.5 text-sm text-foreground leading-relaxed whitespace-pre-line">{issue.probable_cause}</p>
                </div>
                <div>
                  <div className="eyebrow">Contrôles conseillés</div>
                  <p className="mt-1.5 text-sm text-foreground leading-relaxed whitespace-pre-line">{issue.recommended_action}</p>
                </div>
              </div>
              {issue.detail && (
                <div className="mt-4 p-3 bg-muted rounded-md text-xs text-muted-foreground leading-relaxed">
                  <span className="font-semibold text-foreground">Note technique :</span> {issue.detail}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

function PaywallSection({ preview, onBuy, loading, onUnlock }) {
  const [showCode, setShowCode] = useState(false);
  const [code, setCode] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  const submitCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setUnlocking(true);
    try {
      await onUnlock(code.trim());
    } finally {
      setUnlocking(false);
    }
  };

  return (
    <div className="relative">
      {(preview || []).length > 0 && (
        <div className="space-y-4 mb-6">
          {preview.map((p, idx) => (
            <IssueCard key={idx} issue={{ ...p, probable_cause: "", recommended_action: "" }} />
          ))}
        </div>
      )}
      <Card className="p-10 border-primary/30 bg-primary/5 relative overflow-hidden" data-testid="paywall-card">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white text-xs font-semibold border border-primary/20">
              <Lock className="h-3 w-3" /> Contenu premium
            </span>
            <h3 className="mt-4 font-display text-2xl lg:text-3xl font-bold tracking-tight">
              Débloquez votre rapport détaillé
            </h3>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-md">
              Accédez à la liste complète des causes probables, aux actions correctives détaillées,
              aux coûts estimés et aux notes techniques pour chaque problème.
            </p>
            <ul className="mt-4 space-y-2 text-sm">
              <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Toutes les causes probables</li>
              <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Actions concrètes à mener</li>
              <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Accès permanent au rapport</li>
            </ul>
          </div>
          <div className="text-center lg:text-right">
            <div className="font-display text-5xl font-black text-primary">9,99 €</div>
            <div className="text-xs text-muted-foreground mt-1">Paiement unique — sans abonnement</div>
            <Button onClick={onBuy} disabled={loading} size="lg" className="mt-6 rounded-full h-12 px-8" data-testid="paywall-buy-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Débloquer maintenant <ArrowRight className="h-4 w-4 ml-2" /></>}
            </Button>
            <div className="mt-3 text-xs text-muted-foreground">
              ou <Link to="/pricing" className="text-primary underline">choisissez l'abonnement Premium</Link>
            </div>
          </div>
        </div>

        {/* Beta unlock code */}
        <div className="mt-8 pt-6 border-t border-primary/15">
          {!showCode ? (
            <button
              type="button"
              onClick={() => setShowCode(true)}
              className="text-xs text-muted-foreground hover:text-primary underline underline-offset-2"
              data-testid="show-unlock-code-btn"
            >
              J'ai un code de déverrouillage bêta
            </button>
          ) : (
            <form onSubmit={submitCode} className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
              <label htmlFor="unlock-code" className="text-xs text-muted-foreground shrink-0">
                Code bêta :
              </label>
              <input
                id="unlock-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoFocus
                placeholder="Entrez le code"
                className="flex-1 sm:max-w-xs h-9 px-3 rounded-md border border-black/10 bg-white text-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="unlock-code-input"
              />
              <Button type="submit" size="sm" disabled={unlocking} className="rounded-full" data-testid="unlock-code-submit">
                {unlocking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Valider"}
              </Button>
              <button type="button" onClick={() => { setShowCode(false); setCode(""); }} className="text-xs text-muted-foreground hover:text-foreground">
                Annuler
              </button>
            </form>
          )}
        </div>
      </Card>
    </div>
  );
}


function PhotoUploadSection({ diagId, diag, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [photoUrls, setPhotoUrls] = useState({}); // index -> blob url
  const inputRef = useRef(null);
  const photos = diag.photos || [];
  const maxReached = photos.length >= 3;

  useEffect(() => {
    // Fetch each photo as blob (auth-required) and build blob URLs
    const urls = {};
    const fetches = photos.map((_, idx) =>
      api
        .get(`/diagnostic/${diagId}/photos/${idx}`, { responseType: "blob" })
        .then((res) => {
          urls[idx] = URL.createObjectURL(res.data);
        })
        .catch(() => {})
    );
    Promise.all(fetches).then(() => setPhotoUrls(urls));
    return () => {
      Object.values(urls).forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos.length, diagId]);

  const pickFile = () => inputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Format non supporté (JPG, PNG ou WebP)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo trop lourde (max 5 Mo)");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(`/diagnostic/${diagId}/photos`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data);
      toast.success("Photo ajoutée");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="p-6 border-black/10 h-full" data-testid="photos-card">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="eyebrow">Photos</div>
          <h3 className="mt-1 font-display text-lg font-semibold">Complétez avec des photos</h3>
        </div>
        <span className="text-xs text-muted-foreground">
          {photos.length} / 3
        </span>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed">
        Ajoutez jusqu'à 3 photos (bloc technique, écran d'erreur, fuite…) pour aider
        à préparer l'intervention. JPG/PNG/WebP · 5 Mo max.
      </p>

      <div className="mt-4 grid grid-cols-3 gap-3">
        {photos.map((photo, idx) => (
          <div
            key={idx}
            className="aspect-square rounded-lg border border-black/10 bg-muted/40 overflow-hidden grid place-items-center relative"
            data-testid={`photo-thumb-${idx}`}
          >
            {photoUrls[idx] ? (
              <img
                src={photoUrls[idx]}
                alt={photo.filename || `Photo ${idx + 1}`}
                className="h-full w-full object-cover"
              />
            ) : (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            )}
          </div>
        ))}
        {!maxReached && (
          <button
            type="button"
            onClick={pickFile}
            disabled={uploading}
            data-testid="add-photo-btn"
            className="aspect-square rounded-lg border-2 border-dashed border-black/15 bg-background hover:border-primary/40 hover:bg-primary/5 transition-colors grid place-items-center"
          >
            {uploading ? (
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            ) : (
              <div className="flex flex-col items-center gap-1 text-muted-foreground">
                <ImagePlus className="h-5 w-5" />
                <span className="text-[10px] font-semibold uppercase tracking-wider">Ajouter</span>
              </div>
            )}
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileChange}
        className="hidden"
        data-testid="photo-file-input"
      />
    </Card>
  );
}

function AIVisionSection({ diagId, diag, onChange }) {
  const [running, setRunning] = useState(false);
  const ai = diag.ai_analysis;

  const runAnalysis = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/diagnostic/${diagId}/analyze`);
      onChange(data);
      toast.success("Analyse IA terminée");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRunning(false);
    }
  };

  const confidenceColor =
    (ai?.confidence ?? 0) >= 75 ? "bg-emerald-500"
    : (ai?.confidence ?? 0) >= 50 ? "bg-amber-500" : "bg-red-500";

  return (
    <div className="mt-12" data-testid="ai-vision-section">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow flex items-center gap-2">
            <Wand2 className="h-3 w-3" /> IA Vision · Claude Sonnet 4.5
          </div>
          <h2 className="mt-1 font-display text-2xl font-bold">Analyse experte</h2>
        </div>
        {!running && (
          <Button
            onClick={runAnalysis}
            variant={ai ? "outline" : "default"}
            className="rounded-full"
            data-testid="ai-vision-run-btn"
          >
            <Brain className="h-4 w-4 mr-2" />
            {ai ? "Relancer l'analyse" : "Lancer l'analyse IA"}
          </Button>
        )}
      </div>

      {!ai && !running && (
        <Card className="p-8 border-primary/20 bg-primary/5">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <h3 className="font-display text-lg font-semibold">
                Faites analyser vos photos + description par une IA experte
              </h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed max-w-xl">
                Notre modèle Claude Sonnet 4.5 examine vos photos (écran d'erreur, bloc technique,
                fuite…) et votre description pour affiner le diagnostic, détecter les codes erreur
                visibles et proposer des actions ciblées à votre marque de spa.
              </p>
            </div>
            <div className="text-xs text-muted-foreground text-right">
              Inclus dans votre rapport débloqué
            </div>
          </div>
        </Card>
      )}

      {running && (
        <Card className="p-10 border-black/10 flex flex-col items-center justify-center gap-3" data-testid="ai-vision-loading">
          <div className="relative">
            <div className="h-12 w-12 rounded-full bg-primary/10 grid place-items-center">
              <Brain className="h-6 w-6 text-primary" />
            </div>
            <span className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-white bg-primary animate-pulse" />
          </div>
          <p className="text-sm font-semibold">Analyse en cours…</p>
          <p className="text-xs text-muted-foreground">Ceci prend 5 à 20 secondes selon le nombre de photos.</p>
        </Card>
      )}

      {ai && !running && (
        <Card className="p-8 border-black/10" data-testid="ai-vision-result">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="md:w-64 shrink-0">
              <div className="eyebrow">Confiance IA</div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-display text-5xl font-black">{ai.confidence}</span>
                <span className="text-lg text-muted-foreground">/100</span>
              </div>
              <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className={`h-full ${confidenceColor} transition-all`}
                  style={{ width: `${ai.confidence}%` }}
                />
              </div>
              <div className="mt-4 text-xs font-semibold uppercase tracking-widest">
                {ai.matches_rule_based ? (
                  <span className="text-emerald-700">✓ Corrobore le pré-diagnostic</span>
                ) : (
                  <span className="text-amber-700">! Diagnostic affiné différent</span>
                )}
              </div>
              {diag.ai_analysis_at && (
                <p className="mt-4 text-[10px] text-muted-foreground uppercase tracking-widest">
                  Analysé le {new Date(diag.ai_analysis_at).toLocaleString("fr-FR")}
                </p>
              )}
            </div>

            <div className="flex-1 space-y-6">
              <div>
                <div className="eyebrow">Diagnostic affiné</div>
                <p className="mt-1.5 font-display text-lg font-semibold" data-testid="ai-refined-diagnosis">
                  {ai.refined_diagnosis || "—"}
                </p>
              </div>

              {ai.detected_error_codes?.length > 0 && (
                <div>
                  <div className="eyebrow">Codes erreur détectés sur les photos</div>
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="ai-error-codes">
                    {ai.detected_error_codes.map((c) => (
                      <span
                        key={c}
                        className="inline-flex items-center gap-1 rounded-md bg-red-100 text-red-700 border border-red-200 px-3 py-1 text-xs font-bold font-mono"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {ai.visible_anomalies?.length > 0 && (
                <div>
                  <div className="eyebrow">Anomalies visibles</div>
                  <ul className="mt-2 space-y-1.5 text-sm">
                    {ai.visible_anomalies.map((a, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {ai.recommendations?.length > 0 && (
                <div>
                  <div className="eyebrow">Actions ciblées recommandées</div>
                  <ol className="mt-2 space-y-2 text-sm list-decimal list-inside" data-testid="ai-recommendations">
                    {ai.recommendations.map((r, i) => (
                      <li key={i} className="leading-relaxed">{r}</li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}


function SuggestedPartsSection({ parts }) {
  const CATEGORY_META = {
    heater: { icon: Flame, color: "bg-orange-100 text-orange-700" },
    sensor: { icon: Gauge, color: "bg-blue-100 text-blue-700" },
    pump: { icon: Droplets, color: "bg-cyan-100 text-cyan-700" },
    filter: { icon: Waves, color: "bg-emerald-100 text-emerald-700" },
    controller: { icon: Cpu, color: "bg-indigo-100 text-indigo-700" },
    keypad: { icon: Cpu, color: "bg-purple-100 text-purple-700" },
    hydraulic: { icon: Wrench, color: "bg-slate-100 text-slate-700" },
    electric: { icon: Zap, color: "bg-amber-100 text-amber-700" },
    ozone: { icon: Sparkles, color: "bg-teal-100 text-teal-700" },
    chemistry: { icon: Droplets, color: "bg-fuchsia-100 text-fuchsia-700" },
  };

  return (
    <div className="mt-12" data-testid="suggested-parts">
      <div className="mb-6">
        <div className="eyebrow">Pièces à commander</div>
        <h2 className="mt-1 font-display text-2xl font-bold">
          Les {parts.length} pièces les plus probables pour cette panne
        </h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
          Références génériques compatibles avec la majorité des spas. Cliquez pour trouver la pièce
          exacte sur notre partenaire tubs.fr.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="suggested-parts-grid">
        {parts.map((p) => {
          const meta = CATEGORY_META[p.category] || { icon: Wrench, color: "bg-slate-100 text-slate-700" };
          const Icon = meta.icon;
          return (
            <a
              key={p.key}
              href={p.tubs_url}
              target="_blank"
              rel="noopener noreferrer nofollow sponsored"
              className="group"
              data-testid={`suggested-part-${p.key}`}
              data-track-context="diagnostic_result"
              data-track-part-type={p.key}
              data-track-brand={p.brand || ""}
              data-track-part-ref={p.ref || ""}
            >
              <Card className="p-5 border-black/10 hover:border-primary/40 hover:shadow-md transition-all h-full">
                <div className="flex items-start gap-3">
                  <span className={`h-10 w-10 rounded-lg grid place-items-center shrink-0 ${meta.color}`}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        Priorité {p.priority}
                      </span>
                      <span className="text-[10px] font-semibold text-emerald-700">
                        {p.estimated_price}
                      </span>
                    </div>
                    <h3 className="mt-1 font-semibold text-sm group-hover:text-primary transition-colors">
                      {p.label}
                    </h3>
                    <div className="mt-2 text-xs font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1">
                      Chercher sur tubs.fr <ArrowRight className="h-3 w-3" />
                    </div>
                  </div>
                </div>
              </Card>
            </a>
          );
        })}
      </div>
    </div>
  );
}


function NextActions({ diag }) {
  const tubsUrl = diag?.result?.tubs_affiliate_url;
  const techLink = diag?.spa_brand ? `/technicians?brand=${diag.spa_brand}` : "/technicians";

  return (
    <div className="mt-12" data-testid="next-actions">
      <div className="mb-6">
        <div className="eyebrow">Prochaines étapes</div>
        <h2 className="mt-1 font-display text-2xl font-bold">Passer à l'action</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {tubsUrl && (
          <Card className="p-6 border-black/10 hover:border-primary/30 transition-colors" data-testid="tubs-card">
            <ShoppingBag className="h-6 w-6 text-primary" />
            <h3 className="mt-3 font-display text-lg font-semibold">Trouver la pièce détachée</h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              Sondes, réchauffeurs, cartes, joints, filtres — commandez chez notre partenaire
              spécialiste tubs.fr avec livraison rapide.
            </p>
            <Button asChild className="mt-5 rounded-full" data-testid="tubs-cta">
              <a href={tubsUrl} target="_blank" rel="noopener noreferrer sponsored">
                Voir sur tubs.fr <ArrowRight className="h-4 w-4 ml-2" />
              </a>
            </Button>
          </Card>
        )}
        <Card className="p-6 border-black/10 hover:border-primary/30 transition-colors" data-testid="technicians-card">
          <MapPin className="h-6 w-6 text-primary" />
          <h3 className="mt-3 font-display text-lg font-semibold">Trouver un technicien près de chez vous</h3>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
            {diag.spa_brand
              ? <>Techniciens certifiés <span className="capitalize font-semibold">{diag.spa_brand}</span> avec intervention à domicile.</>
              : "Techniciens certifiés avec intervention à domicile, filtrables par marque et département."}
          </p>
          <Button asChild variant="outline" className="mt-5 rounded-full" data-testid="find-technician-cta">
            <Link to={techLink}>Voir l'annuaire <ArrowRight className="h-4 w-4 ml-2" /></Link>
          </Button>
        </Card>
      </div>
    </div>
  );
}

